import { tooltipContainer } from '@/utils/portalContainer'
import { css } from '@emotion/react'
import { Popover, type PopoverProps } from '@mantine/core'
import { merge } from 'lodash-es'
import { motion } from 'motion/react'
import { type ReactNode, useEffect } from 'react'

export interface TooltipProps extends Omit<PopoverProps, 'children'> {
	children: ReactNode
	content: ReactNode
	withArrow?: boolean
	position?: PopoverProps['position']
	disabled?: boolean
	autoHide?: boolean
	autoHideDelay?: number
}

export default function Tooltip({
	children,
	content,
	withArrow = true,
	position = 'top',
	disabled = false,
	autoHide = false,
	autoHideDelay = 5000,
	opened,
	onClose,
	...props
}: TooltipProps) {
	useEffect(() => {
		if (autoHide && opened) {
			const timer = setTimeout(() => {
				onClose?.()
			}, autoHideDelay)
			return () => clearTimeout(timer)
		}
	}, [autoHide, autoHideDelay, opened, onClose])

	if (disabled) {
		return <>{children}</>
	}

	return (
		<Popover
			position={position}
			withArrow={withArrow}
			floatingStrategy="fixed"
			opened={opened}
			onClose={onClose}
			portalProps={{
				target: tooltipContainer,
			}}
			unstyled
			{...props}
			styles={merge(
				{
					arrow: {
						backgroundColor: 'var(--bg-jike-yellow)',
					},
				},
				props.styles
			)}
		>
			<Popover.Target>{children}</Popover.Target>
			<Popover.Dropdown
				css={css`
					background-color: var(--bg-jike-yellow);
					padding: 8px 12px;
					border: none;
					border-radius: 6px;
					position: fixed;
				`}
			>
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
					{content}
				</motion.div>
			</Popover.Dropdown>
		</Popover>
	)
}
